import { FC } from 'react';
import theme from 'config/theme';
import { ENV_CONFIG } from 'config/environment';
import { Link } from 'components/Link';
import { Image } from 'components/Image';
import Icon, { IconNames } from 'components/Icon';
import { SupportOathCTA } from 'components/SupportOathCTA';
import { FormContent } from 'screens/Forms/FormContent';
import { getPathWithRef } from 'helpers/utils';
import { footerLinkSections } from './constants';
import classNames from 'classnames';
import DigiDemsLogo from 'assets/images/footer/digidems.png';
import FederacyLogo from 'assets/images/footer/federacy.svg';
import PciDssLogo from 'assets/images/footer/pcidss.png';

interface Props {
  whiteLabeled?: boolean;
  showSupportCta?: boolean;
}

const linkProps = {
  $color: theme.colors.white,
  $hideUnderline: true,
} as const;

export const Footer: FC<Props> = ({ whiteLabeled, showSupportCta }) => {
  return (
    <>
      {!whiteLabeled && <div className="footer-divider" />}
      <div
        className={classNames('footer-container', {
          'bg-whitelabeled': whiteLabeled,
        })}
      >
        {showSupportCta && !whiteLabeled && (
          <div className="footer-support-cta">
            <SupportOathCTA
              variant="dark"
              eyebrow="Support Oath"
              headline="Keep the recommendations coming."
              body="We don't charge a platform fee. Oath's operations run on separate, direct support."
              buttonText="Give to Oath"
              placement="footer"
              disclaimer="Contributions to Oath fund our research and operations. They are not contributions to a candidate, committee, or the Close the Gap slate."
            />
          </div>
        )}
        <div
          className={classNames({ 'whitelabeled-footer-content': whiteLabeled }, 'footer-content')}
        >
          {whiteLabeled ? (
            <>
              <div className="footer-whitelabeled-support">
                <span className="footer-label body-small-text semi-bold">
                  {footerLinkSections.find(s => s.title === 'SUPPORT')?.title ?? 'SUPPORT'}
                </span>
                <div className="footer-whitelabeled-links">
                  {footerLinkSections
                    .find(s => s.title === 'SUPPORT')
                    ?.links.map(link => {
                      const isInternal = link.url.startsWith('/');
                      return isInternal ? (
                        <Link key={link.label} {...linkProps} to={getPathWithRef(link.url)}>
                          {link.label}
                        </Link>
                      ) : (
                        <Link key={link.label} {...linkProps} href={link.url} target="_blank">
                          {link.label}
                        </Link>
                      );
                    })}
                </div>
              </div>
              <div className="footer-whitelabeled-powered">
                <span className="footer-whitelabeled-powered-label eyebrow-text">POWERED BY</span>
                <Image
                  className="footer-whitelabeled-logo-icon"
                  alt="Oath logo"
                  src={`${
                    ENV_CONFIG().CLOUD_STORAGE.URL
                  }/static_content/oath_logos/Oath_FullLockup_White.png`}
                />
              </div>
            </>
          ) : (
            <>
              <div className="footer-column" id="footer-signup">
                <Image
                  className="footer-logo"
                  alt="Oath logo"
                  src={`${
                    ENV_CONFIG().CLOUD_STORAGE.URL
                  }/static_content/oath_logos/Oath_FullLockup_Color_LightText.svg`}
                />
                <span className="footer-label body-small-text">
                  Make an impact and eliminate spam. Sign up here.
                </span>
                <FormContent form="footer-signup" />
                <div className="row">
                  <a
                    href="https://www.instagram.com/oath.vote/"
                    aria-label="Instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon
                      name={IconNames.Instagram}
                      size={22}
                      color={theme.colors.white}
                      style={{ cursor: 'pointer' }}
                    />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/giveoath"
                    aria-label="LinkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon
                      name={IconNames.Linkedin}
                      size={22}
                      color={theme.colors.white}
                      style={{ cursor: 'pointer' }}
                    />
                  </a>
                </div>
              </div>
              <div className="footer-link-columns">
                {footerLinkSections.map(section => (
                  <div key={section.title} className="footer-column">
                    <span className="footer-label body-small-text semi-bold">{section.title}</span>
                    {section.links.map(link => {
                      const isInternal = link.url.startsWith('/');
                      return isInternal ? (
                        <Link key={link.label} {...linkProps} to={getPathWithRef(link.url)}>
                          {link.label}
                        </Link>
                      ) : (
                        <Link key={link.label} {...linkProps} href={link.url} target="_blank">
                          {link.label}
                        </Link>
                      );
                    })}
                  </div>
                ))}
                <div className="footer-column footer-secured-by-column">
                  <span className="footer-label footer-secured-by-label">Secured by</span>
                  <img
                    src={PciDssLogo}
                    alt="PCI DSS Compliant"
                    className="footer-secured-by-pcidss"
                  />
                  <img src={FederacyLogo} alt="Federacy" className="footer-secured-by-federacy" />
                  <img src={DigiDemsLogo} alt="DigiDems" className="footer-secured-by-digidems" />
                </div>
              </div>
            </>
          )}
        </div>
        <div className={classNames('policy-container', { mt0: whiteLabeled })}>
          <span className="body-small-text color-white">
            © All Rights Reserved {new Date().getFullYear().toString()}. Not authorized or
            controlled by any candidate, candidate’s agent, campaign committee, or party committee.
            No candidate is responsible for our activities. Pol. Adv. Prepared and paid for by 51C
            Collaborative, 8 The Greene, Suite B, Dover, DE 19901; Brian Derrick, CEO.
          </span>
        </div>
      </div>
    </>
  );
};
